/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import { useAppDispatch, useAppSelector } from '@/app/lib/store/hooks';
import { clearUser } from '@/app/lib/store/user-slice';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { getCookie } from 'cookies-next/client';

import { openDialog } from '@/app/lib/store/auth-dialog-slice';
import { CHECK_SESSION_INTERVAL } from '@/app/lib/constants/lifetimes';
import { setProtectedRoute } from '@/app/lib/store/protected-route-slice';
import { parseProtectedRoute } from '@/app/lib/schemas/types/parsers';
import ClientThemeProvider from '../../providers/client-theme-provider';
import SessionExpiredDialogContainer from '../session/session-expired-dialog-container';
import { ERROR_MESSAGES } from '@/app/lib/constants/error-messages';
import {
  ACCOUNT_ROUTES,
  PREMIUM_ROUTES,
  PROTECTED_ROUTES
} from '@/app/lib/constants/app-urls';
import { ENDPOINTS } from '@/app/lib/constants/endpoints';
import { configEndpoint } from '@/app/lib/utils/api-utils';

export default function SessionManager() {
  const pathname = usePathname();

  const onPremiumRoute = useMemo(
    () => pathname.split('/').some(segment => PREMIUM_ROUTES.includes(segment)),
    [pathname]
  );

  const onAccountRoute = useMemo(
    () => pathname.split('/').some(segment => ACCOUNT_ROUTES.includes(segment)),
    [pathname]
  );

  const onProtectedRoute = useMemo(
    () =>
      pathname.split('/').some(segment => PROTECTED_ROUTES.includes(segment)),
    [pathname]
  );

  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user.data);

  useEffect(() => {
    const matchedRoute = PROTECTED_ROUTES.find(route =>
      pathname.includes(route)
    );
    dispatch(setProtectedRoute(parseProtectedRoute(matchedRoute)));
  }, [dispatch, pathname]);

  useEffect(() => {
    const checkSession = async () => {
      const subscriptionStatus = getCookie('subscription_status');

      // Истекла сессия авторизованного пользователя
      if (user && !subscriptionStatus) {
        try {
          const endpoint = configEndpoint(ENDPOINTS.logout);

          await fetch(endpoint, {
            method: 'POST'
          });
          // Очищаем Redux Store
          dispatch(clearUser());
        } catch (error) {
          console.log(
            error instanceof Error ? error.message : ERROR_MESSAGES.logoutError
          );
        }
        if (onProtectedRoute) {
          dispatch(openDialog());
        }
      }
    };

    const intervalId: NodeJS.Timeout = setInterval(
      checkSession,
      CHECK_SESSION_INTERVAL
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [dispatch, pathname, user, onProtectedRoute]);

  return (
    <ClientThemeProvider>
      <SessionExpiredDialogContainer />
    </ClientThemeProvider>
  );
}
